// Wave 1 onboarding modal sequence — first sign-in flow.
//
// Per design/UI : UX v1.0/for-engineering-consumer-wave1/specs/
// consumer-portal-strings.md §B.
//
// Steps: Welcome → Contract (gated, no skip) → Profile (suggested,
// skippable) → First-framework suggestion (skippable) → Done.
// 4-dot progress indicator on steps 1–4.
//
// Trigger: SPA-level — when /auth/me returns
//   session.contractAcceptance.needsAcceptance OR
//   !session.onboarding.profileComplete
// the SPA mounts this on top of the shell. On completion, the SPA
// refetches /auth/me to update session and dismiss.
//
// Contract step uses <CoachingContractView mode="onboarding"/> which POSTs
// to /me/coaching-contract/accept on accept.

import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, TextInput } from '@gc-coach/design-system';
import type { TenantUserProfile } from '@gc-coach/shared';
import { patchProfile } from '../api/endpoints.js';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { CoachingContractView } from './CoachingContractView.js';
import styles from './OnboardingFlow.module.css';

type Step = 'welcome' | 'contract' | 'profile' | 'first-framework' | 'done';

export interface OnboardingFlowProps {
  /** Initial step. Defaults to 'welcome' for first-time; 'contract' if
   *  contract acceptance is the only thing missing (returning user). */
  initialStep?: Step;
  /** Called when the user finishes (or skips through) all steps. Parent
   *  should refetch /auth/me and dismiss the modal. */
  onComplete: () => void;
}

export function OnboardingFlow({ initialStep = 'welcome', onComplete }: OnboardingFlowProps) {
  const [step, setStep] = useState<Step>(initialStep);
  const { t } = useI18n();

  function nextStep() {
    if (step === 'welcome') setStep('contract');
    else if (step === 'contract') setStep('profile');
    else if (step === 'profile') setStep('first-framework');
    else if (step === 'first-framework') setStep('done');
    else onComplete();
  }

  // Back navigates one step backwards. Contract is a hard gate — once
  // accepted, you don't un-accept from a Back click (Profile → Welcome
  // skips over Contract rather than inviting re-review). Welcome has no
  // Back (first step). Done also has no Back (terminal celebration).
  function backStep() {
    if (step === 'profile') setStep('welcome');
    else if (step === 'first-framework') setStep('profile');
  }

  const canGoBack = step === 'profile' || step === 'first-framework';

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.modal}>
        {canGoBack && (
          <button type="button" className={styles.backLink} onClick={backStep}>
            ← {t('common.back')}
          </button>
        )}
        {step === 'welcome' && <WelcomeStep onContinue={nextStep} />}
        {step === 'contract' && <ContractStep onAccepted={nextStep} />}
        {step === 'profile' && <ProfileStep onContinue={nextStep} />}
        {step === 'first-framework' && (
          <FirstFrameworkStep onContinue={nextStep} onSkip={() => setStep('done')} />
        )}
        {step === 'done' && <DoneStep onComplete={onComplete} />}
        {step !== 'welcome' && step !== 'done' && <ProgressDots active={stepIndex(step)} />}
      </div>
    </div>
  );
}

function stepIndex(step: Step): number {
  switch (step) {
    case 'welcome':
      return 0;
    case 'contract':
      return 1;
    case 'profile':
      return 2;
    case 'first-framework':
      return 3;
    case 'done':
      return 4;
  }
}

// ─── Welcome step ─────────────────────────────────────────────────────────

function WelcomeStep({ onContinue }: { onContinue: () => void }) {
  const { t, tt } = useI18n();
  const { session } = useSession();
  const firstName = (session?.displayName?.split(' ')[0] ?? '').trim();

  return (
    <StepLayout
      eyebrow={t('onboarding.welcome.eyebrow')}
      title={
        firstName
          ? tt('onboarding.welcome.headlineNamed', { firstName })
          : t('onboarding.welcome.headlineFallback')
      }
      body={t('onboarding.welcome.body')}
    >
      <div className={styles.actions}>
        <Button kind="primary" onClick={onContinue}>
          {t('onboarding.welcome.continueCta')}
        </Button>
      </div>
    </StepLayout>
  );
}

// ─── Contract step ────────────────────────────────────────────────────────

function ContractStep({ onAccepted }: { onAccepted: () => void }) {
  const { t } = useI18n();
  return (
    <div className={styles.stepContractWrap}>
      <span className={styles.stepBadge}>{t('onboarding.contract.stepBadge')}</span>
      <CoachingContractView mode="onboarding" onAccepted={onAccepted} />
    </div>
  );
}

// ─── Profile step ─────────────────────────────────────────────────────────

const YEARS_OPTIONS: ReadonlyArray<Exclude<TenantUserProfile['yearsExperience'], undefined>> = [
  '0-3',
  '4-7',
  '8-15',
  '15+',
];

function ProfileStep({ onContinue }: { onContinue: () => void }) {
  const { t } = useI18n();
  const [roleTitle, setRoleTitle] = useState('');
  const [yearsExperience, setYearsExperience] = useState<
    Exclude<TenantUserProfile['yearsExperience'], undefined> | ''
  >('');
  const [teamSize, setTeamSize] = useState('');
  const [industrySector, setIndustrySector] = useState('');
  const [jurisdictions, setJurisdictions] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function saveAndContinue(skip: boolean) {
    if (skip) {
      onContinue();
      return;
    }
    const profile: TenantUserProfile = {};
    if (roleTitle.trim()) profile.roleTitle = roleTitle.trim();
    if (yearsExperience) profile.yearsExperience = yearsExperience;
    if (teamSize.trim() && !Number.isNaN(Number(teamSize))) {
      profile.teamSize = Math.floor(Number(teamSize));
    }
    if (industrySector.trim()) profile.industrySector = industrySector.trim();
    const jurisCodes = jurisdictions
      .split(/[,\s]+/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => /^[A-Z]{2}$/.test(s));
    if (jurisCodes.length) profile.jurisdictions = jurisCodes;

    if (Object.keys(profile).length === 0) {
      onContinue();
      return;
    }

    setSubmitting(true);
    await patchProfile({ profile });
    setSubmitting(false);
    onContinue();
  }

  return (
    <StepLayout
      eyebrow={t('onboarding.profile.stepBadge')}
      title={t('onboarding.profile.title')}
      body={t('onboarding.profile.intro')}
    >
      <div className={styles.formGrid}>
        <TextInput
          label={t('onboarding.profile.roleTitleLabel')}
          value={roleTitle}
          onChange={(e) => setRoleTitle(e.target.value)}
          placeholder={t('onboarding.profile.roleTitlePlaceholder')}
        />
        <div className={styles.field}>
          <span className={styles.fieldLabel}>
            {t('onboarding.profile.yearsExperienceLabel')}
          </span>
          <div className={styles.yearsChips}>
            {YEARS_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={[
                  styles.yearsChip,
                  yearsExperience === opt ? styles.yearsChipActive : null,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setYearsExperience(opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
        <TextInput
          label={t('onboarding.profile.teamSizeLabel')}
          value={teamSize}
          onChange={(e) => setTeamSize(e.target.value)}
          placeholder={t('onboarding.profile.teamSizePlaceholder')}
          inputMode="numeric"
        />
        <TextInput
          label={t('onboarding.profile.industryLabel')}
          value={industrySector}
          onChange={(e) => setIndustrySector(e.target.value)}
          placeholder={t('onboarding.profile.industryPlaceholder')}
        />
        <TextInput
          label={t('onboarding.profile.jurisdictionsLabel')}
          value={jurisdictions}
          onChange={(e) => setJurisdictions(e.target.value)}
          placeholder={t('onboarding.profile.jurisdictionsPlaceholder')}
          hint={t('onboarding.profile.jurisdictionsHint')}
        />
      </div>
      <div className={styles.actionsSplit}>
        <Button kind="ghost" onClick={() => void saveAndContinue(true)} disabled={submitting}>
          {t('onboarding.profile.skipCta')}
        </Button>
        <Button kind="primary" onClick={() => void saveAndContinue(false)} disabled={submitting}>
          {t('onboarding.profile.continueCta')}
        </Button>
      </div>
    </StepLayout>
  );
}

// ─── First-framework suggestion step ─────────────────────────────────────

function FirstFrameworkStep({
  onContinue,
  onSkip,
}: {
  onContinue: () => void;
  onSkip: () => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <StepLayout
      eyebrow={t('onboarding.firstFramework.stepBadge')}
      title={t('onboarding.firstFramework.title')}
      body={t('onboarding.firstFramework.intro')}
    >
      <div className={styles.recommendationCard}>
        <span className={styles.recommendationTag}>
          {t('onboarding.firstFramework.suggestedDomain')}
        </span>
        <span className={styles.recommendationName}>
          {t('onboarding.firstFramework.suggestedName')}
        </span>
        <span className={styles.recommendationOneLiner}>
          {t('onboarding.firstFramework.suggestedOneLiner')}
        </span>
      </div>
      <div className={styles.actionsSplit}>
        {/* Secondary: Skip ahead to Done without engaging the recommendation.
          * Matches the "SUGGESTED" step badge — Wave-1 spec explicitly flags
          * this step as skippable. */}
        <Button kind="ghost" onClick={onSkip}>
          {t('onboarding.firstFramework.skipCta')}
        </Button>
        <Button
          kind="ghost"
          onClick={() => {
            onContinue();
            navigate('/frameworks');
          }}
        >
          {t('onboarding.firstFramework.browseCta')}
        </Button>
        {/* Activate-now is wired to the Library for now since the
          * recommendation engine isn't in V1; swap the href when real
          * profile → framework matching lands. */}
        <Button
          kind="primary"
          onClick={() => {
            onContinue();
            navigate('/frameworks');
          }}
        >
          {t('onboarding.firstFramework.activateCta')}
        </Button>
      </div>
    </StepLayout>
  );
}

// ─── Done step ─────────────────────────────────────────────────────────────

function DoneStep({ onComplete }: { onComplete: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  return (
    <StepLayout
      eyebrow={t('onboarding.done.eyebrow')}
      title={t('onboarding.done.title')}
      body={t('onboarding.done.body')}
    >
      <div className={styles.actionsSplit}>
        <Button
          kind="ghost"
          onClick={() => {
            onComplete();
            navigate('/coach');
          }}
        >
          {t('onboarding.done.askCoachCta')}
        </Button>
        <Button
          kind="primary"
          onClick={() => {
            onComplete();
            navigate('/dashboard');
          }}
        >
          {t('onboarding.done.dashboardCta')}
        </Button>
      </div>
    </StepLayout>
  );
}

// ─── Shared step layout + progress ────────────────────────────────────────

function StepLayout({
  eyebrow,
  title,
  body,
  children,
}: {
  eyebrow: string;
  title: string;
  body: string;
  children: ReactNode;
}) {
  return (
    <div className={styles.step}>
      <header className={styles.stepHeader}>
        <span className={styles.eyebrow}>{eyebrow}</span>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.body}>{body}</p>
      </header>
      <div className={styles.stepBody}>{children}</div>
    </div>
  );
}

function ProgressDots({ active }: { active: number }) {
  return (
    <div className={styles.progress} aria-label={`Step ${active} of 4`}>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={[styles.dot, i <= active ? styles.dotActive : null]
            .filter(Boolean)
            .join(' ')}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}
